import { usePortfolio } from '../hooks/usePortfolio';
import {
  formatLamportsToSOL,
  formatAvgEntry,
} from '../helpers/portfolioFormatters';
import './Portfolio.css';

function Portfolio({ walletConnected, walletPublicKey }) {
  const { positions, summary, loading, error, refresh } = usePortfolio(walletPublicKey);

  // Empty state: no wallet connected
  if (!walletConnected) {
    return (
      <section className="page-section portfolio-page">
        <div className="portfolio-page__header">
          <h1 className="portfolio-page__title">
            <span role="img" aria-label="portfolio">
              📊
            </span>{' '}
            My Portfolio
          </h1>
          <p className="portfolio-page__subtitle">
            View your trading history and performance across all games
          </p>
        </div>

        <div className="portfolio-empty-state">
          <div className="portfolio-empty-state__icon">🔌</div>
          <h2 className="portfolio-empty-state__title">Connect Your Wallet</h2>
          <p className="portfolio-empty-state__message">
            Please connect your wallet to view your portfolio
          </p>
        </div>
      </section>
    );
  }

  // Loading state
  if (loading) {
    return (
      <section className="page-section portfolio-page">
        <div className="portfolio-page__header">
          <h1 className="portfolio-page__title">
            <span role="img" aria-label="portfolio">
              📊
            </span>{' '}
            My Portfolio
          </h1>
          <p className="portfolio-page__subtitle">
            View your trading history and performance across all games
          </p>
        </div>

        <div className="portfolio-loading">
          <p>Loading portfolio...</p>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="page-section portfolio-page">
        <div className="portfolio-page__header">
          <h1 className="portfolio-page__title">
            <span role="img" aria-label="portfolio">
              📊
            </span>{' '}
            My Portfolio
          </h1>
          <p className="portfolio-page__subtitle">
            View your trading history and performance across all games
          </p>
        </div>

        <div className="portfolio-error">
          <h3 className="portfolio-error__title">Error Loading Portfolio</h3>
          <p className="portfolio-error__message">{error}</p>
        </div>

        <button className="portfolio-refresh-button" onClick={refresh}>
          Try Again
        </button>
      </section>
    );
  }

  // Empty state: no positions
  if (positions.length === 0) {
    return (
      <section className="page-section portfolio-page">
        <div className="portfolio-page__header">
          <h1 className="portfolio-page__title">
            <span role="img" aria-label="portfolio">
              📊
            </span>{' '}
            My Portfolio
          </h1>
          <p className="portfolio-page__subtitle">
            View your trading history and performance across all games
          </p>
        </div>

        <div className="portfolio-empty-state">
          <div className="portfolio-empty-state__icon">📭</div>
          <h2 className="portfolio-empty-state__title">No Positions Yet</h2>
          <p className="portfolio-empty-state__message">
            Start trading on the Home page to see your positions here
          </p>
        </div>
      </section>
    );
  }

  // Determine P&L styling
  const totalRealizedPnL = summary.totalRealizedPnL;
  const pnlClass =
    totalRealizedPnL > 0 ? 'summary-card__value--positive' : totalRealizedPnL < 0 ? 'summary-card__value--negative' : '';

  return (
    <section className="page-section portfolio-page">
      <div className="portfolio-page__header">
        <h1 className="portfolio-page__title">
          <span role="img" aria-label="portfolio">
            📊
          </span>{' '}
          My Portfolio
        </h1>
        <p className="portfolio-page__subtitle">
          View your trading history and performance across all games
        </p>
      </div>

      {/* Summary Cards */}
      <div className="portfolio-summary">
        <div className="summary-card">
          <p className="summary-card__label">All Time Realized P&L</p>
          <h2 className={`summary-card__value ${pnlClass}`}>
            {totalRealizedPnL >= 0 ? '+' : ''}
            {formatLamportsToSOL(totalRealizedPnL, 4)}
            <span className="summary-card__suffix">SOL</span>
          </h2>
        </div>

        <div className="summary-card">
          <p className="summary-card__label">Total Games</p>
          <h2 className="summary-card__value">{summary.totalGames}</h2>
        </div>

        <div className="summary-card">
          <p className="summary-card__label">Open Positions</p>
          <h2 className="summary-card__value">{summary.openPositions}</h2>
        </div>

        <div className="summary-card">
          <p className="summary-card__label">Closed Positions</p>
          <h2 className="summary-card__value">{summary.closedPositions}</h2>
        </div>
      </div>

      {/* Portfolio Table */}
      <div className="portfolio-table-container">
        <div className="portfolio-table-header">
          <h2>Position History</h2>
          <button
            className="portfolio-refresh-button"
            onClick={refresh}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <table className="portfolio-table">
          <thead>
            <tr>
              <th>Game ID</th>
              <th>Status</th>
              <th>Average Entry</th>
              <th>Total SOL Spent</th>
              <th>Total SOL Received</th>
              <th>Realized P&L</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((position) => {
              const realizedPnL = Number(position.realized_pnl_lamports || 0);
              const pnlRowClass =
                realizedPnL > 0
                  ? 'pnl-positive'
                  : realizedPnL < 0
                    ? 'pnl-negative'
                    : 'pnl-neutral';

              return (
                <tr key={position.id}>
                  <td>{position.game_id || 'N/A'}</td>
                  <td>
                    <span
                      className={
                        position.position_status === 'open'
                          ? 'status-badge status-badge--open'
                          : 'status-badge status-badge--closed'
                      }
                    >
                      {position.position_status || 'N/A'}
                    </span>
                  </td>
                  <td>{formatAvgEntry(position.avg_entry_lamports_per_token)} SOL</td>
                  <td>{formatLamportsToSOL(position.total_sol_spent_lamports)} SOL</td>
                  <td>{formatLamportsToSOL(position.total_sol_received_lamports)} SOL</td>
                  <td className={pnlRowClass}>
                    {realizedPnL >= 0 ? '+' : ''}
                    {formatLamportsToSOL(realizedPnL)} SOL
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default Portfolio;
